<template>
  <div class="dashboard-bg min-vh-100 py-4 px-2 px-md-0 animate__animated animate__fadeIn">
    <div class="container" style="max-width: 860px;">

      <!-- ── Greeting bar ── -->
      <div class="d-flex align-items-center gap-3 mb-4">
        <div class="avatar-circle">
          <i class="fas fa-user"></i>
        </div>
        <div>
          <div class="text-muted small">Bun venit,</div>
          <div class="fw-bold fs-5 text-primary">{{ fullName || 'Client' }}</div>
        </div>
      </div>

      <!-- ── Profil Client ── -->
      <div class="card dash-card shadow-sm mb-4">
        <div class="card-header dash-card-header">
          <i class="fas fa-id-card me-2"></i> Profil Client
        </div>
        <div class="card-body p-3 p-md-4">
          <div class="row g-3">
            <div class="col-12 col-md-6">
              <label class="form-label fw-semibold small text-muted text-uppercase ls-1">Nume complet</label>
              <div class="input-group">
                <span class="input-group-text bg-white border-end-0">
                  <i class="fas fa-user text-primary"></i>
                </span>
                <input v-model="fullName" class="form-control border-start-0 ps-0" placeholder="Numele tău" />
              </div>
            </div>
            <div class="col-12 col-md-6">
              <label class="form-label fw-semibold small text-muted text-uppercase ls-1">Telefon</label>
              <div class="input-group">
                <span class="input-group-text bg-white border-end-0">
                  <i class="fas fa-phone text-primary"></i>
                </span>
                <input v-model="phone" type="tel" class="form-control border-start-0 ps-0" placeholder="+40 7xx xxx xxx" />
              </div>
            </div>
            <div class="col-12">
              <label class="form-label fw-semibold small text-muted text-uppercase ls-1">Parolă nouă <span class="fw-normal text-muted">(lasă gol pentru a nu schimba)</span></label>
              <div class="input-group">
                <span class="input-group-text bg-white border-end-0">
                  <i class="fas fa-lock text-primary"></i>
                </span>
                <input v-model="password" :type="showPass ? 'text' : 'password'" class="form-control border-start-0 ps-0 border-end-0" placeholder="Parolă nouă" />
                <button class="input-group-text bg-white border-start-0" @click="showPass = !showPass" type="button" tabindex="-1">
                  <i :class="['fas', showPass ? 'fa-eye-slash' : 'fa-eye', 'text-muted']"></i>
                </button>
              </div>
            </div>
          </div>

          <div class="mt-4 d-flex justify-content-end">
            <button class="btn btn-primary px-4" @click="saveChanges">
              <i class="fas fa-save me-2"></i> Salvează modificările
            </button>
          </div>
        </div>
      </div>

      <!-- ── Chat ── -->
      <div class="card dash-card shadow-sm">
        <div class="card-header dash-card-header d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2">
          <span><i class="fas fa-comments me-2"></i> Conversație cu Consultantul</span>
          <div class="d-flex gap-2 w-100 w-sm-auto">
            <select v-model="selectedConversation" class="form-select form-select-sm flex-grow-1" style="max-width: 200px;">
              <option v-for="conv in conversations" :value="conv.id" :key="conv.id">
                {{ new Date(conv.created_at).toLocaleDateString('ro-RO') }}
              </option>
            </select>
            <button class="btn btn-sm btn-outline-light flex-shrink-0" @click="startNewConversation" title="Conversație nouă">
              <i class="fas fa-plus"></i>
            </button>
          </div>
        </div>

        <!-- Messages -->
        <div class="chat-scroll px-3 py-3" ref="chatScrollEl">
          <div v-if="messages.length === 0" class="text-center text-muted py-5">
            <i class="fas fa-comment-dots fa-2x mb-3 d-block opacity-25"></i>
            <span class="small">Nicio conversație încă. Trimite primul mesaj!</span>
          </div>

          <div
            v-for="(msg, index) in messages"
            :key="index"
            class="chat-row mb-3"
            :class="msg.from === 'client' ? 'chat-row--right' : 'chat-row--left'"
          >
            <!-- Avatar -->
            <div class="chat-avatar" :class="msg.from === 'client' ? 'chat-avatar--client' : 'chat-avatar--consultant'">
              <i :class="['fas', msg.from === 'client' ? 'fa-user' : 'fa-headset']"></i>
            </div>

            <div class="chat-bubble" :class="msg.from === 'client' ? 'chat-bubble--client' : 'chat-bubble--consultant'">
              <div class="chat-text">{{ msg.message }}</div>

              <!-- Atașamente -->
              <div v-if="msg.attachments && msg.attachments.length > 0" class="mt-2 pt-2 border-top border-white border-opacity-25">
                <a
                  v-for="file in msg.attachments"
                  :key="file.url"
                  :href="cleanUrl(file.url)"
                  target="_blank"
                  :download="file.title"
                  class="attach-link d-flex align-items-center gap-1"
                >
                  <i class="fas fa-paperclip fa-sm"></i>
                  <span>{{ file.title }}</span>
                </a>
              </div>

              <div class="chat-time">{{ formatDate(msg.timestamp) }}</div>
            </div>
          </div>
        </div>

        <!-- Input -->
        <div class="card-footer chat-footer p-3">
          <div class="chat-input-area mb-2">
            <input
              v-model="message"
              class="form-control chat-input border-0 bg-transparent"
              placeholder="Scrie un mesaj..."
              @keyup.enter="sendMessage"
            />
          </div>
          <div class="d-flex gap-2 align-items-center">
            <label class="file-attach-btn flex-shrink-0" title="Atașează fișier">
              <i class="fas fa-paperclip"></i>
              <input ref="fileInput" type="file" class="d-none" @change="handleFileUpload" />
            </label>
            <span v-if="uploadedFile" class="small text-muted text-truncate flex-grow-1">
              📎 {{ uploadedFile.name }}
            </span>
            <span v-else class="flex-grow-1"></span>
            <button class="btn btn-primary btn-sm px-3 flex-shrink-0" @click="sendMessage" :disabled="!message.trim() && !uploadedFile">
              <i class="fas fa-paper-plane me-1"></i> Trimite
            </button>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, nextTick } from 'vue'
import api from '@/api/axios'
import Swal from 'sweetalert2'

const clientId = parseInt(localStorage.getItem('client_id'))
if (!clientId || isNaN(clientId)) {
  Swal.fire('Eroare!', 'Nu ești autentificat. Te rugăm să te loghezi din nou.', 'error')
  window.location.href = '/client/login'
}

const fullName = ref('')
const phone = ref('')
const password = ref('')
const showPass = ref(false)

const conversations = ref([])
const selectedConversation = ref(null)
const messages = ref([])
const message = ref('')
const fileInput = ref(null)
const uploadedFile = ref(null)
const chatScrollEl = ref(null)

const scrollToBottom = async () => {
  await nextTick()
  if (chatScrollEl.value) {
    chatScrollEl.value.scrollTop = chatScrollEl.value.scrollHeight
  }
}

const fetchClientData = async () => {
  const res = await api.get(`/client/info/${clientId}/`)
  fullName.value = res.data.full_name
  phone.value = res.data.phone
}

const fetchConversations = async () => {
  const res = await api.get(`/client/conversations/${clientId}/`)
  conversations.value = res.data
  if (res.data.length > 0) selectedConversation.value = res.data[0].id
}

const startNewConversation = async () => {
  const res = await api.post('/client/start-conversation/', { client_id: clientId })
  selectedConversation.value = res.data.id
  conversations.value.unshift({ id: res.data.id, created_at: res.data.created_at })
}

const handleFileUpload = (event) => {
  uploadedFile.value = event.target.files[0]
}

const fetchMessages = async () => {
  if (!selectedConversation.value) return
  const res = await api.get(`/client/conversation/${selectedConversation.value}/messages/`)
  messages.value = res.data
  scrollToBottom()
}

const saveChanges = async () => {
  await api.post('/client/update/', {
    id: clientId,
    full_name: fullName.value,
    phone: phone.value,
    password: password.value
  })
  Swal.fire({ icon: 'success', title: 'Salvat!', text: 'Datele au fost actualizate.', timer: 2000, showConfirmButton: false })
}

const cleanUrl = (url) => {
  const base = api.defaults.baseURL.replace(/\/api\/?$/, '')
  return `${base}${url}`
}

const sendMessage = async () => {
  if (!message.value.trim() && !uploadedFile.value) return
  if (!selectedConversation.value) return

  const msgRes = await api.post(`/client/conversation/${selectedConversation.value}/send/`, {
    client_id: clientId,
    message: message.value
  })

  const newMsg = {
    from: 'client',
    message: message.value,
    timestamp: new Date().toISOString(),
    attachments: []
  }

  if (uploadedFile.value) {
    const formData = new FormData()
    formData.append('file', uploadedFile.value)
    formData.append('title', uploadedFile.value.name)
    await api.post(`/client/conversation/message/${msgRes.data.message_id}/attach/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    newMsg.attachments = [{ url: '', title: uploadedFile.value.name }]
  }

  messages.value.push(newMsg)
  message.value = ''
  uploadedFile.value = null
  if (fileInput.value) fileInput.value.value = ''
  scrollToBottom()
}

const formatDate = (ts) => new Date(ts).toLocaleString('ro-RO', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })

watch(selectedConversation, fetchMessages)

onMounted(async () => {
  await fetchClientData()
  await fetchConversations()
  if (conversations.value.length === 0) await startNewConversation()
  await fetchMessages()
})
</script>

<style scoped>
/* ── Override Bootstrap primary cu culoarea site-ului ── */
:deep(.btn-primary),
.btn-primary {
  background-color: rgb(0, 75, 107) !important;
  border-color: rgb(0, 75, 107) !important;
}
.btn-primary:hover {
  background-color: rgb(0, 60, 88) !important;
  border-color: rgb(0, 60, 88) !important;
}
.btn-outline-primary {
  color: rgb(0, 75, 107) !important;
  border-color: rgb(0, 75, 107) !important;
}
.btn-outline-primary:hover {
  background-color: rgb(0, 75, 107) !important;
  color: white !important;
}
.text-primary {
  color: rgb(0, 75, 107) !important;
}

/* ── Layout ── */
.dashboard-bg {
  background: #f8f9fa;
}

/* ── Avatar greeting ── */
.avatar-circle {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgb(0, 75, 107);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  flex-shrink: 0;
}

/* ── Card ── */
.dash-card {
  border: none;
  border-radius: 1rem;
  overflow: hidden;
}

.dash-card-header {
  background: rgb(0, 75, 107);
  color: white;
  font-weight: 600;
  padding: 0.85rem 1.1rem;
  font-size: 0.95rem;
}

/* ── Input group tweaks ── */
.input-group-text {
  border-color: #dee2e6;
  color: #6c757d;
}
.form-control:focus {
  box-shadow: none;
  border-color: rgb(0, 75, 107);
}

/* ── Chat ── */
.chat-scroll {
  height: 380px;
  overflow-y: auto;
  scroll-behavior: smooth;
}

.chat-row {
  display: flex;
  gap: 8px;
  align-items: flex-end;
}
.chat-row--right {
  flex-direction: row-reverse;
}

.chat-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  flex-shrink: 0;
}
.chat-avatar--client {
  background: rgb(0, 75, 107);
  color: white;
}
.chat-avatar--consultant {
  background: #198754;
  color: white;
}

.chat-bubble {
  max-width: 72%;
  padding: 0.55rem 0.8rem;
  border-radius: 1rem;
  font-size: 0.9rem;
  line-height: 1.45;
}
.chat-bubble--client {
  background: rgb(0, 75, 107);
  color: white;
  border-bottom-right-radius: 0.2rem;
}
.chat-bubble--consultant {
  background: #e9f7ef;
  color: #155724;
  border: 1px solid #c3e6cb;
  border-bottom-left-radius: 0.2rem;
}

.chat-time {
  font-size: 0.7rem;
  margin-top: 4px;
  text-align: right;
  opacity: 0.65;
}

.attach-link {
  color: inherit;
  text-decoration: underline;
  font-size: 0.8rem;
  opacity: 0.85;
}
.attach-link:hover {
  opacity: 1;
}

/* ── Footer input ── */
.chat-footer {
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
}

.chat-input-area {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 0.6rem;
  overflow: hidden;
}

.chat-input {
  padding: 0.55rem 0.85rem;
  font-size: 0.92rem;
}
.chat-input:focus {
  outline: none;
  box-shadow: none;
}

.file-attach-btn {
  width: 36px;
  height: 36px;
  border: 1px solid #dee2e6;
  border-radius: 50%;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #6c757d;
  transition: all 0.2s;
}
.file-attach-btn:hover {
  background: #e7f0ff;
  color: rgb(0, 75, 107);
  border-color: rgb(0, 75, 107);
}

/* ── Misc ── */
.ls-1 {
  letter-spacing: 0.04em;
}

@media (max-width: 576px) {
  .chat-bubble {
    max-width: 85%;
    font-size: 0.85rem;
  }
  .chat-scroll {
    height: 300px;
  }
}
</style>